import { useCallback, useEffect, useState } from 'react';

/**
 * Which side panels are on screen (prd-deltas §23). Both can be hidden at any width.
 * On a wide screen they are columns beside the canvas, and the choice is remembered
 * in this browser. On a narrow one they open over the canvas, one at a time, and
 * start closed, because the canvas is what a phone has room for.
 */

/** Kept in step with the narrow layout in shell.css. */
export const NARROW_QUERY = '(max-width: 900px)';

export type Pane = 'tree' | 'inspector';
export type Panes = Record<Pane, boolean>;

const STORE_KEY = 'civil.panes';
const CLOSED: Panes = { tree: false, inspector: false };

export function useMediaQuery(query: string): boolean {
  const [hit, setHit] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const list = window.matchMedia(query);
    const onChange = () => setHit(list.matches);
    onChange();
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);
  return hit;
}

function storedPanes(): Panes {
  try {
    const raw = JSON.parse(window.localStorage.getItem(STORE_KEY) ?? 'null') as Partial<Panes> | null;
    return { tree: raw?.tree !== false, inspector: raw?.inspector !== false };
  } catch {
    return { tree: true, inspector: true };
  }
}

function storePanes(panes: Panes) {
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(panes));
  } catch {
    // Storage refused (a private window): the choice lasts the page, which is enough.
  }
}

export function usePanes() {
  const narrow = useMediaQuery(NARROW_QUERY);
  const [wide, setWide] = useState<Panes>(storedPanes);
  const [over, setOver] = useState<Panes>(CLOSED);
  const panes = narrow ? over : wide;

  /** Shows or hides one panel; `to` forces it. On a narrow screen showing one hides the other. */
  const setPane = useCallback(
    (pane: Pane, to?: boolean) => {
      if (narrow) {
        setOver((cur) => ({ ...CLOSED, [pane]: to ?? !cur[pane] }));
      } else {
        setWide((cur) => {
          const next = { ...cur, [pane]: to ?? !cur[pane] };
          storePanes(next);
          return next;
        });
      }
    },
    [narrow],
  );

  /** Closes whatever is open over the canvas; on a wide screen the columns stay. */
  const closeOver = useCallback(() => setOver(CLOSED), []);

  return { narrow, panes, setPane, closeOver };
}
